import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Download, Search, MoreVertical } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CustStatusBadge } from "@/components/badges";
import { CustomerStatusDialog } from "@/components/CustomerStatusDialog";
import { CustomerHistoryDialog } from "@/components/CustomerHistoryDialog";
import { SupplyDialog } from "@/components/SupplyDialog";
import { CUST_STATUS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, NativeSelect, Loading } from "@/components/common";
import { AddressList } from "@/pages/tenant/AddressList";
import { ImportDialog } from "@/components/ImportDialog";
import { DeleteCustomerButton } from "@/components/DeleteCustomerButton";
import { TrashList } from "@/components/TrashList";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, downloadCsv } from "@/lib/api";

const BLANK = { name: "", phone: "", address_id: "", address_detail: "", notes: "", active: true, status: "baru" };

function RowMenu({ c, i, editable, onAction }) {
  const st = c.status || "aktif";
  const item = (mode, label) => <DropdownMenuItem onSelect={() => onAction(mode, c)} data-testid={`customer-${mode}-${i}`}>{label}</DropdownMenuItem>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" data-testid={`customer-menu-${i}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {item("history", "Lihat Riwayat Pelanggan")}
        {editable && st !== "berhenti" && item("berhenti", "Ubah Status Menjadi Berhenti")}
        {editable && st === "berhenti" && item("aktif_kembali", "Aktifkan Kembali Pelanggan")}
        {editable && (st === "baru" || st === "aktif_kembali") && item("supply", "Input Pasokan Awal")}
        {editable && (st === "baru" || st === "aktif_kembali") && item("aktif", "Tetapkan Status AKTIF")}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CustomerDialog({ open, onOpenChange, initial, addresses, onSave }) {
  const [f, setF] = useState(BLANK);
  useEffect(() => {
    if (open) setF(initial ? { ...BLANK, ...initial, address_id: initial.address_id || "" } : BLANK);
  }, [open, initial]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{initial ? "Ubah Pelanggan" : "Tambah Pelanggan Baru"}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3" data-testid="customer-form">
          <div><Label>Nama Pelanggan</Label><Input value={f.name} onChange={set("name")} required data-testid="customer-name-input" /></div>
          <div><Label>Telepon</Label><Input value={f.phone || ""} onChange={set("phone")} data-testid="customer-phone-input" /></div>
          <div><Label>Alamat (dari daftar alamat)</Label>
            <NativeSelect value={f.address_id} onChange={set("address_id")} className="w-full" data-testid="customer-address-select">
              <option value="">— tanpa alamat —</option>
              {addresses.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </NativeSelect>
          </div>
          <div><Label>Detail Alamat (RT/RW, patokan)</Label><Input value={f.address_detail || ""} onChange={set("address_detail")} data-testid="customer-address-detail-input" /></div>
          <div><Label>Catatan</Label><Input value={f.notes || ""} onChange={set("notes")} data-testid="customer-notes-input" /></div>
          {!initial && (
            <div><Label>Status Pelanggan</Label>
              <NativeSelect value={f.status} onChange={set("status")} className="w-full" data-testid="customer-status-select">
                <option value="baru">PELANGGAN BARU (isi pasokan awal setelah disimpan)</option>
                <option value="aktif">AKTIF (pelanggan lama yang sudah berjualan)</option>
              </NativeSelect>
            </div>
          )}
          <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF((s) => ({ ...s, active: v }))} data-testid="customer-active-switch" /><span className="text-sm">Pelanggan aktif</span></div>
          <DialogFooter><Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" data-testid="customer-form-submit">Simpan</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CustomerCard({ c, i, editable, onAction, onEdit, onDone }) {
  const { can } = useAuth();
  return (
    <div className="border border-slate-300 bg-white rounded-sm p-3 space-y-1.5" data-testid={`customer-card-${i}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium">{c.name}</div>
          <div className="flex flex-wrap items-center gap-1 mt-0.5"><CustStatusBadge status={c.status} />{!c.active && <span className="text-[10px] text-rose-600">(nonaktif)</span>}</div>
        </div>
        <RowMenu c={c} i={`m${i}`} editable={editable} onAction={onAction} />
      </div>
      <div className="text-xs text-slate-600">{[c.address_name, c.address_detail].filter(Boolean).join(" · ") || "-"}</div>
      <div className="flex flex-wrap gap-2 pt-1">
        {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center h-10 px-3 rounded-md border border-emerald-300 text-emerald-800 text-sm font-mono" data-testid={`customer-call-${i}`}>{c.phone}</a>}
        <div className="flex-1" />
        {editable && <Button variant="outline" className="h-10" onClick={() => onEdit(c)} data-testid={`customer-card-edit-${i}`}><Pencil className="h-4 w-4" /></Button>}
        {can("customer:delete") && <DeleteCustomerButton customer={c} idx={`m${i}`} onDone={onDone} />}
      </div>
    </div>
  );
}

function CustomerTable() {
  const { can } = useAuth();
  const editable = can("customer:write");
  const [rows, setRows] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [q, setQ] = useState("");
  const [addr, setAddr] = useState("");
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const [stFilter, setStFilter] = useState("");
  const [statusT, setStatusT] = useState(null);
  const [histT, setHistT] = useState(null);
  const [supT, setSupT] = useState(null);
  const onAction = (mode, c) => {
    if (mode === "history") setHistT(c);
    else if (mode === "supply") setSupT(c);
    else setStatusT({ customer: c, mode });
  };
  const afterStatus = (c, mode) => {
    setStatusT(null);
    load();
    if (mode === "aktif_kembali") setSupT(c);
  };
  const load = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([api.get("/customers", { params: { q, address_id: addr } }), api.get("/addresses")]);
      setRows(c.data);
      setAddresses(a.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [q, addr]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  const save = async (f) => {
    const body = { ...f, address_id: f.address_id || null };
    try {
      const res = dlg.initial ? await api.put(`/customers/${dlg.initial.id}`, body) : await api.post("/customers", body);
      toast.success("Pelanggan disimpan");
      setDlg({ open: false, initial: null });
      load();
      if (!dlg.initial && res.data.status === "baru") setSupT(res.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" /><Input placeholder="Cari nama, telepon, alamat..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-64" data-testid="customer-search-input" /></div>
        <NativeSelect value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="customer-filter-address">
          <option value="">Semua alamat</option>
          {addresses.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </NativeSelect>
        <NativeSelect value={stFilter} onChange={(e) => setStFilter(e.target.value)} data-testid="customer-filter-status">
          <option value="">Semua status</option>
          {Object.entries(CUST_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </NativeSelect>
        <div className="flex-1" />
        {can("export:run") && <Button variant="outline" onClick={() => downloadCsv("/export/customers", "pelanggan")} data-testid="customer-export-btn"><Download className="h-4 w-4 mr-2" />Export CSV</Button>}
        {editable && <ImportDialog kind="customers" onDone={load} />}
        {editable && <Button onClick={() => setDlg({ open: true, initial: null })} className="bg-emerald-700 hover:bg-emerald-800" data-testid="customer-add-btn"><Plus className="h-4 w-4 mr-2" />Tambah Pelanggan Baru</Button>}
      </div>
      {!rows ? <Loading /> : (
        <>
        <div className="md:hidden space-y-2" data-testid="customer-cards">
          {rows.length === 0 && <div className="text-center text-slate-500 py-6 border border-dashed rounded-sm">Belum ada pelanggan</div>}
          {rows.filter((c) => !stFilter || (c.status || "aktif") === stFilter).map((c, i) => (
            <CustomerCard key={c.id} c={c} i={i} editable={editable} onAction={onAction} onEdit={(x) => setDlg({ open: true, initial: x })} onDone={load} />
          ))}
        </div>
        <div className="xl-wrap hidden md:block">
          <table className="xl">
            <thead><tr><th className="rownum">#</th><th>Nama Pelanggan</th><th>Status Pelanggan</th><th>Telepon</th><th>Alamat</th><th>Detail</th><th>Aksi</th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-6">Belum ada pelanggan</td></tr>}
              {rows.filter((c) => !stFilter || (c.status || "aktif") === stFilter).map((c, i) => (
                <tr key={c.id} data-testid={`customer-row-${i}`}>
                  <td className="rownum">{i + 1}</td>
                  <td className="font-medium">{c.name}</td>
                  <td className="whitespace-nowrap"><CustStatusBadge status={c.status} testId={`customer-status-${i}`} />{c.status_date && c.status !== "aktif" && <span className="ml-1 text-[10px] text-slate-500">sejak {c.status_date}</span>}{!c.active && <span className="ml-1 text-[10px] text-rose-600">(nonaktif)</span>}</td>
                  <td className="font-mono text-xs">{c.phone}</td>
                  <td>{c.address_name}</td>
                  <td className="text-slate-600">{c.address_detail}</td>
                  <td className="whitespace-nowrap">
                    <RowMenu c={c} i={i} editable={editable} onAction={onAction} />
                    {editable && <Button size="sm" variant="ghost" onClick={() => setDlg({ open: true, initial: c })} data-testid={`customer-edit-${i}`}><Pencil className="h-4 w-4" /></Button>}
                    {can("customer:delete") && <DeleteCustomerButton customer={c} idx={i} onDone={load} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      <CustomerStatusDialog target={statusT} onClose={() => setStatusT(null)} onDone={afterStatus} />
      <CustomerHistoryDialog customer={histT} onClose={() => setHistT(null)} />
      <SupplyDialog customer={supT} onClose={() => setSupT(null)} onDone={load} />
      <CustomerDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} initial={dlg.initial} addresses={addresses} onSave={save} />
    </div>
  );
}

export default function Customers() {
  const { can } = useAuth();
  return (
    <div>
      <PageHeader sheet="SHEET 02" title="Data Pelanggan" subtitle="Database pelanggan & daftar alamat khusus perusahaan Anda." />
      <Tabs defaultValue="customers">
        <TabsList>
          <TabsTrigger value="customers" data-testid="tab-customers">Pelanggan</TabsTrigger>
          <TabsTrigger value="addresses" data-testid="tab-addresses">Daftar Alamat</TabsTrigger>
          {can("trash:read") && <TabsTrigger value="trash" data-testid="tab-trash">Tempat Sampah</TabsTrigger>}
        </TabsList>
        <TabsContent value="customers" className="mt-4"><CustomerTable /></TabsContent>
        <TabsContent value="addresses" className="mt-4"><AddressList /></TabsContent>
        {can("trash:read") && <TabsContent value="trash" className="mt-4"><TrashList /></TabsContent>}
      </Tabs>
    </div>
  );
}
